import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { numberSeparator, roundOff } from "utils";
import { LineChart } from "components/Chart";
import { Title } from "style/components/Title.styles";
import { calcGainPercentage } from "features/Strategy/utils";
import { NoDataMessage } from "features/Strategy/v2/Detail/StrategyDetails.styles";
import {
  HistoricalPerformanceChartContainer,
  HeaderComponent,
} from "./HistoricalPerformanceChart.styles";
import { HistoricalPerformanceChartPlaceholder } from "./HistoricalPerformanceChartPlaceholder";
import { LegendContent as HpLegendContent } from "./Legend";
import { LegendContent } from "components/Chart/Legend";
import { TIME_INDICATOR_V2 } from "utils/constants";
import { IconProfitArrow } from "assets/icons/index";
import { Filter } from "components/Chart";
import { ChartTooltip, ChartTooltipWithProfit } from "./ChartTooltips";
// import { CustomXAxis } from "./CustomXAxis";
import {
  // CustomYAxis,
  calculateYAxisDomain,
} from "./CustomYAxis";

export const HistoricalPerformanceChartV2 = (props) => {
  const {
    height = "350px",
    lines,
    showHeading,
    timeframe,
    data = [],
    nodata = "No Broker Account Connected or Created",
    isLoading,
    handleFilterClick,
    activePeriod,
    legendContent,
    showLegend = true,
    showTooltipDate = false,
    showTopTooltipProfit = true,
  } = props;

  const [gain, setGain] = useState(0);
  const [benchmark, setBenchmark] = useState(null);
  const [activeValue, setActiveValue] = useState(0);
  const [gainPercentage, setGainPercentage] = useState(0);
  const [benchmarkPerc, setBenchmarkPerc] = useState(0);
  const [isTooltipActive, setIsTooltipActive] = useState(false);

  const dataLen = data.length;
  // Use the first line's key as the strategy key if provided, otherwise fallback for backward compatibility
  const stratKey = (lines && lines[0] && lines[0].key) || "Strategy Value";
  const bmKey = "Benchmark Value (SPY)";
  const firstValue = useMemo(
    () => dataLen && data[0][stratKey],
    [dataLen, data]
  );
  const firstBMValue = useMemo(
    () => dataLen && data[0][bmKey],
    [dataLen, data]
  );
  // Track the latest data point and key to detect dataset changes (e.g., portfolio switch)
  const lastPoint = useMemo(() => {
    if (!dataLen) return null;
    const last = data[dataLen - 1];
    return {
      ts: last?.name,
      strat: last?.[stratKey],
      bm: last?.[bmKey],
    };
  }, [dataLen, data, stratKey]);

  const handleCalcProfitLoss = useCallback(
    (stratVal, bmObj) => {
      const calcGain = stratVal - firstValue;
      setActiveValue(stratVal);
      setGain(calcGain);

      if (firstValue !== 0) {
        const gainPerc = calcGainPercentage(firstValue, stratVal);
        setGainPercentage(roundOff(gainPerc));

        // Benchmark
        if (bmObj && bmObj.value) {
          const calcBm = bmObj.value - firstValue;
          const percentage = calcGainPercentage(firstValue, bmObj.value);
          setBenchmark({
            value: bmObj.value,
            color: bmObj.color,
            gain: calcBm,
            percentage,
          });
          const bmPerc = calcGainPercentage(firstBMValue, bmObj.value);
          if (bmPerc) setBenchmarkPerc(roundOff(bmPerc));
        }
      }
    },
    [firstValue, firstBMValue]
  );

  useEffect(() => {
    if (!dataLen || isTooltipActive || activeValue !== 0) {
      return;
    }
    const lastValue = data[dataLen - 1][stratKey];
    const lastBMValue = data[dataLen - 1][bmKey];
    handleCalcProfitLoss(lastValue, { value: lastBMValue });
  }, [dataLen, isTooltipActive, handleCalcProfitLoss, activeValue]);

  // Ensure header balance (activeValue) reflects the latest point whenever the dataset changes
  // This runs on portfolio changes and new data without requiring user interaction
  const prevSigRef = useRef({
    ts: null,
    strat: null,
    bm: null,
    stratKey: null,
  });
  useEffect(() => {
    if (!lastPoint) return;
    const prev = prevSigRef.current;
    const changed =
      prev.stratKey !== stratKey ||
      prev.ts !== lastPoint.ts ||
      prev.strat !== lastPoint.strat;

    if (changed) {
      handleCalcProfitLoss(lastPoint.strat, { value: lastPoint.bm });
      prevSigRef.current = {
        ts: lastPoint.ts,
        strat: lastPoint.strat,
        bm: lastPoint.bm,
        stratKey,
      };
    }
  }, [stratKey, lastPoint, handleCalcProfitLoss]);

  const onMouseMove = (e) => {
    if (!e) return;
    const { activePayload } = e;

    if (activePayload && activePayload.length > 0) {
      const stratValue = activePayload[0].value;
      const bmObject = activePayload[1];
      handleCalcProfitLoss(stratValue, bmObject);
    }
  };

  const formattedGain = () => {
    if (gain) {
      return numberSeparator(gain)?.replace("-", "");
    }
    return gain;
  };

  const formattedGainPerc = () => {
    if (gainPercentage) {
      return numberSeparator(gainPercentage)?.replace("-", "");
    }
    return gainPercentage;
  };

  const formattedBenchmark = () => {
    if (benchmark?.gain) {
      return numberSeparator(benchmark?.gain)?.replace("-", "");
    }
    return benchmark?.gain;
  };

  const formattedBenchmarkPerc = () => {
    if (benchmark?.percentage) {
      return numberSeparator(benchmark?.percentage)?.replace("-", "");
    }
    return benchmark?.percentage;
  };

  const getLegendContent = () => {
    if (!showLegend) {
      return;
    }

    if (props.tooltipContent) {
      return {
        ...legendContent,
        content: LegendContent,
        wrapperStyle: { marginTop: "15px" },
      };
    }

    return {
      ...legendContent,
      content: (props) => (
        <HpLegendContent
          gainPercentage={gainPercentage}
          gain={gain}
          benchmarkPerc={benchmarkPerc}
          benchmark={benchmark}
          activeValue={activeValue}
          firstValue={firstValue}
          isTooltipActive={isTooltipActive}
          bmKey={bmKey}
          stratKey={stratKey}
          activePeriod={activePeriod}
          {...props}
        />
      ),
      wrapperStyle: { marginTop: "15px" },
    };
  };

  const onMouseEnter = () => setIsTooltipActive(true);
  const onMouseLeave = () => {
    setIsTooltipActive(false);

    // Reset current balance to latest data point when hover ends
    if (dataLen > 0) {
      const lastValue = data[dataLen - 1][stratKey];
      const lastBMValue = data[dataLen - 1][bmKey];
      handleCalcProfitLoss(lastValue, { value: lastBMValue });
    }
  };

  const getTooltipContent = (data) => {
    // setIsTooltipActive(data.active);
    if (showTooltipDate) {
      return <ChartTooltip {...data} activePeriod={activePeriod} />;
    }
    return <ChartTooltipWithProfit {...data} firstValue={firstValue} />;
  };

  if (isLoading) {
    return <HistoricalPerformanceChartPlaceholder {...props} />;
  }

  return (
    <HistoricalPerformanceChartContainer>
      {showHeading && (
        <Title className="line-chart-heading">Historical Performance</Title>
      )}

      {dataLen > 0 && (
        <>
          {/* Chart Header: show timeframe filter always; hide balance/profit if custom tooltip */}
          <div className="investment-value">
            <div className="total">
              <HeaderComponent>
                <div>
                  {(!props.tooltipContent || props.alwaysShowHeaderBalance) && (
                    <>
                      <p className="current-balance">Current Balance</p>
                      <div className="amount">
                        $
                        {`${numberSeparator(activeValue)}`.includes(".") ? (
                          <>
                            {`${numberSeparator(activeValue)}`.split(".")[0]}
                            <span className="metric-value-decimal">
                              .{`${numberSeparator(activeValue)}`.split(".")[1]}
                            </span>
                          </>
                        ) : (
                          `${numberSeparator(activeValue)}`
                        )}
                      </div>
                    </>
                  )}
                </div>
                {timeframe && (
                  <Filter
                    timeframe={timeframe}
                    activePeriod={activePeriod}
                    handleFilterClick={handleFilterClick}
                    className="h-performance-filter"
                  />
                )}
              </HeaderComponent>
              {!props.tooltipContent && showTopTooltipProfit && (
                <>
                  <div className="extra-info">
                    <span
                      className={classNames({
                        gain: activeValue >= firstValue,
                        loss: activeValue < firstValue,
                      })}
                    >
                      <span>{stratKey} </span>${formattedGain() || "0"}
                    </span>
                    <div
                      className={classNames("percentage", {
                        gain: activeValue >= firstValue,
                        loss: activeValue < firstValue,
                      })}
                    >
                      {activeValue >= firstValue ? <IconProfitArrow /> : "-"}{" "}
                      {formattedGainPerc()}%
                    </div>
                    <span className="date">
                      {TIME_INDICATOR_V2[activePeriod.toUpperCase()]}
                    </span>
                  </div>
                  {benchmark && (
                    <div className="extra-info benchmark">
                      <span className="benchmark-wrapper">
                        <span>{bmKey} </span>
                        {benchmark?.value >= firstBMValue ? "+" : "-"}$
                        {formattedBenchmark()}
                        <div className=" benchmark">
                          {benchmark?.value >= firstBMValue ? "+" : "-"}{" "}
                          {formattedBenchmarkPerc()}%
                        </div>
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Chart */}
          <div
            style={{ width: "100%", height, position: "relative" }}
            className="chart-wrapper"
          >
            {/* Custom Y-Axis */}
            {/* <CustomYAxis data={data} height={parseInt(height)} /> */}

            {/* Custom X-Axis */}
            {/* <CustomXAxis data={data} activePeriod={activePeriod} /> */}

            {/* LineChart with synced Y-axis domain */}
            <LineChart
              data={data}
              lines={lines}
              lineChartProps={{ onMouseMove, onMouseEnter, onMouseLeave }}
              tooltipContent={props.tooltipContent || getTooltipContent}
              showAreaFill={true}
              margin={{
                top: 0,
                right: 20,
                left: -50,
                bottom: 0,
              }}
              xAxisProps={false}
              yAxisProps={{
                domain: calculateYAxisDomain(data),
              }}
              {...props}
              legendContent={getLegendContent()}
            />
          </div>
        </>
      )}

      {/* No-Data state: keep original header layout with timeframe filter visible */}
      {!dataLen && (
        <>
          <div className="investment-value">
            <div className="total">
              <HeaderComponent>
                <div />
                {timeframe && (
                  <Filter
                    timeframe={timeframe}
                    activePeriod={activePeriod}
                    handleFilterClick={handleFilterClick}
                    className="h-performance-filter"
                  />
                )}
              </HeaderComponent>
            </div>
          </div>
          <div style={{ width: "100%", height }} className="chart-wrapper">
            {(() => {
              const isTimeframeSpecificNoData =
                Array.isArray(timeframe) &&
                timeframe.length > 0 &&
                !!activePeriod;
              const noDataText = isTimeframeSpecificNoData
                ? "No Data for selected timeframe"
                : nodata;
              return (
                <NoDataMessage className="no-data">{noDataText}</NoDataMessage>
              );
            })()}
          </div>
        </>
      )}
    </HistoricalPerformanceChartContainer>
  );
};

HistoricalPerformanceChartV2.propTypes = {
  height: PropTypes.string,
  data: PropTypes.array,
  lines: PropTypes.array,
  showHeading: PropTypes.bool,
  nodata: PropTypes.string,
  isLoading: PropTypes.bool,
  timeframe: PropTypes.array,
  handleFilterClick: PropTypes.func,
  activePeriod: PropTypes.string,
  tooltipContent: PropTypes.any,
  legendContent: PropTypes.any,
  showLegend: PropTypes.bool,
  showTooltipDate: PropTypes.bool,
  showTopTooltipProfit: PropTypes.bool,
  alwaysShowHeaderBalance: PropTypes.bool,
};
